import React, { useState, useEffect, useRef } from 'react';
import { X, Search } from 'lucide-react';
import { isCommandApiEnabled, fetchPatients } from '../api/commandApi';
import type { PatientListItem } from '../api/commandApi';
import { isSmartModeActive } from '../api/smartApi';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';

/** Display shape: API patient + optional recent flag (all from API are non-recent for now). */
type PatientRow = PatientListItem & { recent?: boolean };

interface PatientPickerModalProps {
  open: boolean;
  onClose: () => void;
  onSelectPatient: (patient: { name: string; mrn: string; status: string; risk: string }) => void;
}

export function PatientPickerModal({ open, onClose, onSelectPatient }: PatientPickerModalProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [patients, setPatients] = useState<PatientRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open || isSmartModeActive()) return;
    setError(null);
    if (isCommandApiEnabled()) {
      setLoading(true);
      fetchPatients('default')
        .then((list) => setPatients(list.map((p) => ({ ...p, recent: false }))))
        .catch((err: unknown) => {
          const msg = err instanceof Error ? err.message : '';
          if (msg.toLowerCase().includes('authentication required')) {
            setError('Authentication required. Open the app with a valid access token or sign in first.');
            return;
          }
          setError('Failed to load patients from the API. Is March API running (for example at http://localhost:8000)?');
        })
        .finally(() => setLoading(false));
    } else {
      setPatients([]);
    }
  }, [open]);

  // SMART mode locks the workspace to one patient — no picker at all. Outside
  // it, the Dialog root stays mounted and `open` drives visibility, so Radix
  // can run its close sequence (focus restore to the trigger).
  if (isSmartModeActive()) return null;

  const filteredPatients = patients.filter(
    (patient) =>
      patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient.mrn.includes(searchTerm)
  );

  const handleSelect = (patient: any) => {
    onSelectPatient(patient);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        data-testid="patient-picker-modal"
        showCloseButton={false}
        aria-describedby={undefined}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          searchInputRef.current?.focus();
        }}
        className="flex max-h-[90vh] w-full max-w-2xl flex-col gap-0 overflow-hidden border-border bg-surface-raised p-0 sm:max-h-[80vh] sm:max-w-2xl sm:rounded-xl"
      >
          <div className="border-b border-border px-3 sm:px-6 py-2 sm:py-4 flex items-center justify-between">
            <DialogTitle className="text-sm sm:text-lg font-medium text-foreground">Select Patient</DialogTitle>
            <button onClick={onClose} aria-label="Close patient picker" className="text-text-tertiary hover:text-text-primary p-0.5 sm:p-1">
              <X className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>

          <div className="px-3 sm:px-6 py-2 sm:py-4 border-b border-border">
            <div className="relative">
              <Search className="absolute left-2 sm:left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 sm:w-5 sm:h-5 text-text-tertiary" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name or MRN..."
                aria-label="Search patients by name or MRN"
                className="w-full pl-8 sm:pl-10 pr-2 sm:pr-4 py-1.5 sm:py-2 text-[11px] sm:text-base border border-border bg-surface-raised text-foreground placeholder:text-text-tertiary rounded-md sm:rounded-lg focus:outline-hidden focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading && (
              <div className="px-3 sm:px-6 py-6 sm:py-8 text-center text-text-tertiary text-sm">
                Loading patients…
              </div>
            )}
            {!loading && error && (
              <div className="px-3 sm:px-6 py-6 sm:py-8 text-center text-destructive-text text-sm">
                {error}
              </div>
            )}
            {!loading && !error && filteredPatients.length === 0 && (
              <div className="px-3 sm:px-6 py-6 sm:py-8 text-center text-text-tertiary text-sm">
                {isCommandApiEnabled()
                  ? 'No patients in the database. Ensure the backend is running and seeded.'
                  : 'Set VITE_MARCH_API_URL to the backend URL (e.g. http://localhost:8000) to load patients from the API.'}
              </div>
            )}
            {!loading && filteredPatients.filter(p => p.recent).length > 0 && (
              <div>
                <div className="px-3 sm:px-6 py-1.5 sm:py-2 text-[10px] sm:text-xs font-medium text-text-tertiary uppercase tracking-wide bg-surface-sunken border-b border-border">
                  Recent Patients
                </div>
                {filteredPatients
                  .filter((p) => p.recent)
                  .map((patient) => (
                    <button
                      key={patient.mrn}
                      data-testid={`patient-row-${patient.mrn}`}
                      onClick={() => handleSelect(patient)}
                      className="w-full px-3 sm:px-6 py-2 sm:py-4 text-left hover:bg-surface-hover border-b border-border flex items-center justify-between gap-1.5 sm:gap-4 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] sm:text-base font-medium text-foreground mb-0.5 sm:mb-1 truncate">
                          {patient.name}
                        </div>
                        <div className="flex items-center gap-1.5 sm:gap-3 text-[9px] sm:text-sm text-text-tertiary flex-wrap">
                          <span>MRN {patient.mrn}</span>
                          <span className="hidden sm:inline">•</span>
                          <span className="truncate">{patient.status}</span>
                        </div>
                      </div>
                      <span
                        className={`px-1.5 sm:px-3 py-0.5 sm:py-1 rounded-full text-[9px] sm:text-xs font-medium shrink-0 ${
                          patient.risk === 'High'
                            ? 'bg-status-negative-bg text-status-negative-text border border-(--status-negative)/30'
                            : patient.risk === 'Moderate'
                            ? 'bg-status-attention-bg text-status-attention-text border border-(--status-attention)/30'
                            : 'bg-status-positive-bg text-status-positive-text border border-(--status-positive)/30'
                        }`}
                      >
                        {patient.risk} Risk
                      </span>
                    </button>
                  ))}
              </div>
            )}

            {!loading && filteredPatients.filter(p => !p.recent).length > 0 && (
              <div>
                <div className="px-3 sm:px-6 py-1.5 sm:py-2 text-[10px] sm:text-xs font-medium text-text-tertiary uppercase tracking-wide bg-surface-sunken border-b border-border">
                  All Patients
                </div>
                {filteredPatients
                  .filter((p) => !p.recent)
                  .map((patient) => (
                    <button
                      key={patient.mrn}
                      data-testid={`patient-row-${patient.mrn}`}
                      onClick={() => handleSelect(patient)}
                      className="w-full px-3 sm:px-6 py-2 sm:py-4 text-left hover:bg-surface-hover border-b border-border flex items-center justify-between gap-1.5 sm:gap-4 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] sm:text-base font-medium text-foreground mb-0.5 sm:mb-1 truncate">
                          {patient.name}
                        </div>
                        <div className="flex items-center gap-1.5 sm:gap-3 text-[9px] sm:text-sm text-text-tertiary flex-wrap">
                          <span>MRN {patient.mrn}</span>
                          <span className="hidden sm:inline">•</span>
                          <span className="truncate">{patient.status}</span>
                        </div>
                      </div>
                      <span
                        className={`px-1.5 sm:px-3 py-0.5 sm:py-1 rounded-full text-[9px] sm:text-xs font-medium shrink-0 ${
                          patient.risk === 'High'
                            ? 'bg-status-negative-bg text-status-negative-text border border-(--status-negative)/30'
                            : patient.risk === 'Moderate'
                            ? 'bg-status-attention-bg text-status-attention-text border border-(--status-attention)/30'
                            : 'bg-status-positive-bg text-status-positive-text border border-(--status-positive)/30'
                        }`}
                      >
                        {patient.risk} Risk
                      </span>
                    </button>
                  ))}
              </div>
            )}
          </div>
      </DialogContent>
    </Dialog>
  );
}
